/**
 * URL-merge helpers for the mobile filter modal.
 *
 * The Amazzing backend serializes multi-select facets as a SINGLE
 * comma-separated query param (e.g. `material-dominujacy=bawelna,len`) and
 * OR-combines the values. Repeated keys (`material-dominujacy=bawelna&material-dominujacy=len`)
 * are NOT understood — the backend silently keeps only the last occurrence.
 *
 * The desktop filter UI is fine because it navigates the backend-provided
 * `targetUrl` of each value directly. The mobile modal, however, batches
 * several selections before applying, so it has to merge those per-value
 * target URLs itself. These helpers do that merge while preserving the
 * backend's comma format (and using set semantics so re-adding/3rd-selection
 * never produces repeated keys).
 */

const FILTER_URL_BASE = "https://lavmag.local";

export function readRelativeFilterUrl(targetUrl: string): URL {
  return new URL(targetUrl || "/", FILTER_URL_BASE);
}

export function serializeRelativeFilterUrl(url: URL): string {
  // URLSearchParams encodes the comma separators as %2C; decode them back so
  // the resulting URL matches the backend's clean comma format.
  const search = url.search.replace(/%2C/gi, ",");
  return `${url.pathname}${search}${url.hash}`;
}

/**
 * Read a param's values as a flat list, treating both comma-separated values
 * and (legacy) repeated keys as multiple values.
 */
function readMultiValues(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter(Boolean);
}

/** Replace a param with a single comma-joined value, or drop it when empty. */
function writeMultiValues(
  params: URLSearchParams,
  key: string,
  values: string[],
): void {
  params.delete(key);
  if (values.length) {
    params.set(key, values.join(","));
  }
}

function uniqueValues(values: string[]): string[] {
  return [...new Set(values)];
}

function sameValueSet(first: string[], second: string[]): boolean {
  if (first.length !== second.length) {
    return false;
  }
  const setSecond = new Set(second);
  return first.every((value) => setSecond.has(value));
}

function syncPathAndHash(target: URL, baseUrl: URL, nextUrl: URL): void {
  if (nextUrl.pathname !== baseUrl.pathname) {
    target.pathname = nextUrl.pathname;
  }
  if (nextUrl.hash !== baseUrl.hash) {
    target.hash = nextUrl.hash;
  }
}

/**
 * Merge a multi-select facet value's backend `targetUrl` into the pending URL,
 * keeping all previously-queued values for the same facet as one comma param.
 *
 * @param shouldSelectValue whether the toggle is turning the value ON (add) or
 *   OFF (remove).
 */
export function mergeMultiSelectTargetUrl(
  currentUrl: string,
  pendingUrl: string | null,
  targetUrl: string,
  shouldSelectValue: boolean,
): string {
  const baseUrl = readRelativeFilterUrl(currentUrl || "/");
  const pending = readRelativeFilterUrl(pendingUrl ?? currentUrl ?? "/");
  const nextUrl = readRelativeFilterUrl(targetUrl);

  syncPathAndHash(pending, baseUrl, nextUrl);

  const keys = new Set([
    ...baseUrl.searchParams.keys(),
    ...nextUrl.searchParams.keys(),
  ]);

  for (const key of keys) {
    const baseValues = readMultiValues(baseUrl.searchParams, key);
    const nextValues = readMultiValues(nextUrl.searchParams, key);

    if (sameValueSet(baseValues, nextValues)) {
      continue;
    }

    const pendingValues = readMultiValues(pending.searchParams, key);

    if (shouldSelectValue) {
      // The value(s) the backend's targetUrl added relative to the current page
      // are the ones being toggled on; union them into the pending set.
      const added = nextValues.filter((value) => !baseValues.includes(value));
      const toAdd = added.length ? added : nextValues;
      writeMultiValues(
        pending.searchParams,
        key,
        uniqueValues([...pendingValues, ...toAdd]),
      );
      continue;
    }

    // Removal: the symmetric difference between base and next is the value(s)
    // being toggled off.
    const toRemove = new Set([
      ...baseValues.filter((value) => !nextValues.includes(value)),
      ...nextValues.filter((value) => !baseValues.includes(value)),
    ]);
    writeMultiValues(
      pending.searchParams,
      key,
      pendingValues.filter((value) => !toRemove.has(value)),
    );
  }

  return serializeRelativeFilterUrl(pending);
}

function hasSameSearchValues(
  first: URLSearchParams,
  second: URLSearchParams,
  key: string,
): boolean {
  const firstValues = first.getAll(key);
  const secondValues = second.getAll(key);

  return (
    firstValues.length === secondValues.length &&
    firstValues.every((value, index) => value === secondValues[index])
  );
}

/**
 * Generic merge for single-select / price / chip-removal targets: copy each
 * changed param from the backend's `targetUrl` verbatim into the pending URL.
 * The backend already encodes the correct post-toggle state (incl. comma
 * multi-value params), so we only need to overlay the keys it changed.
 */
export function mergeFilterTargetUrl(
  currentUrl: string,
  pendingUrl: string | null,
  targetUrl: string,
): string {
  const baseUrl = readRelativeFilterUrl(currentUrl || "/");
  const pending = readRelativeFilterUrl(pendingUrl ?? currentUrl ?? "/");
  const nextUrl = readRelativeFilterUrl(targetUrl);

  syncPathAndHash(pending, baseUrl, nextUrl);

  const keys = new Set([
    ...baseUrl.searchParams.keys(),
    ...nextUrl.searchParams.keys(),
  ]);

  for (const key of keys) {
    if (hasSameSearchValues(baseUrl.searchParams, nextUrl.searchParams, key)) {
      continue;
    }

    pending.searchParams.delete(key);
    for (const value of nextUrl.searchParams.getAll(key)) {
      pending.searchParams.append(key, value);
    }
  }

  return serializeRelativeFilterUrl(pending);
}
